import { memo, useMemo, useState, useEffect, useContext } from 'react'
import { createPortal } from 'react-dom'
import styled from 'styled-components'
import { rem } from 'polished'
import Heading from 'LuxKit/Typography/Heading'
import BodyText from 'LuxKit/Typography/BodyText'
import Image from 'components/Common/Image'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import noop from 'lib/function/noop'
import type { LinkRect } from './types'
import { PREVIEW_WIDTH, VIEWPORT_PADDING } from './calculatePreviewPosition'
import { getMapPreviewPositionStyle } from './getMapPreviewPositionStyle'
import { TripDrawerMapPreviewContext } from 'tripPlanner/components/GenericDrawer/TripDrawerMapPreviewContext'

const PreviewContainer = styled.div<{ $withinDrawer: boolean }>`
  position: ${props => props.$withinDrawer ? 'absolute' : 'fixed'};
  width: ${rem(PREVIEW_WIDTH)};
  max-width: calc(100vw - ${rem(VIEWPORT_PADDING * 2)});
  background-color: ${props => props.theme.palette.neutral.surface.normalBackground};
  border-radius: ${props => props.theme.borderRadius.L};
  box-shadow: ${props => props.theme.shadow.flat.large};
  z-index: 5;
  overflow: hidden;
  cursor: pointer;
  pointer-events: auto;
  animation: fadeInScale 0.15s ease-out;

  @keyframes fadeInScale {
    from {
      opacity: 0;
      transform: scale(0.95) translateY(${rem(4)});
    }
    to {
      opacity: 1;
      transform: scale(1) translateY(0);
    }
  }
`

const ImageContainer = styled.div`
  position: relative;
  width: 100%;
  height: ${rem(223)};
  flex-shrink: 0;
  overflow: hidden;
  background-color: ${props => props.theme.palette.neutral.surface.lightBackground};
`

const ESTIMATED_PREVIEW_HEIGHT = 350
const ESTIMATED_PREVIEW_HEIGHT_WITHOUT_IMAGE = 150

interface Props {
  title: string
  description?: string
  imageIds: Array<string>
  imageId?: string
  linkRect: LinkRect
  onClick?: () => void
  onClose?: () => void
  onMouseEnter?: () => void
}

function WishlistOfferHoverPreview({
  title,
  description,
  imageIds,
  imageId,
  linkRect,
  onClick = noop,
  onClose = noop,
  onMouseEnter: onMouseEnterProp = noop,
}: Props) {
  const drawerPreviewElement = useContext(TripDrawerMapPreviewContext)
  const [hasImageError, setHasImageError] = useState(false)
  const previewImageId = imageIds[0] ?? imageId
  const shouldShowImage = Boolean(previewImageId) && !hasImageError

  useEffect(() => {
    setHasImageError(false)
  }, [previewImageId])

  const positionStyle = useMemo(() => getMapPreviewPositionStyle(
    linkRect,
    shouldShowImage ? ESTIMATED_PREVIEW_HEIGHT : ESTIMATED_PREVIEW_HEIGHT_WITHOUT_IMAGE,
    drawerPreviewElement,
  ), [linkRect, shouldShowImage, drawerPreviewElement])

  const preview = (
    <PreviewContainer
      $withinDrawer={Boolean(drawerPreviewElement)}
      style={positionStyle}
      onMouseEnter={onMouseEnterProp}
      onMouseLeave={onClose}
      onClick={onClick}
    >
      {shouldShowImage && (
        <ImageContainer>
          <Image
            id={previewImageId}
            alt={title}
            width={322}
            height={223}
            gravity="auto"
            fit="center"
            loading="eager"
            onError={() => setHasImageError(true)}
          />
        </ImageContainer>
      )}

      <Sheet padding={16}>
        <Group direction="vertical" gap={4}>
          <Heading variant="heading6">{title}</Heading>
          {description && (
            <BodyText variant="small" colour="light-foreground-alt">
              {description}
            </BodyText>
          )}
        </Group>
      </Sheet>
    </PreviewContainer>
  )

  return drawerPreviewElement ? createPortal(preview, drawerPreviewElement) : preview
}

export default memo(WishlistOfferHoverPreview)
